import { motion } from "framer-motion";
import { ArrowUpRight, Building2, ShieldCheck, Radar, ServerCog, Wifi, Gauge, RadioTower, Network } from "lucide-react";
import { useLanguage } from "@/i18n";
import { scrollToId } from "@/lib/scroll";
import SectionHead, { EASE, reveal } from "@/components/SectionHead";

const CORPORATE_IMG =
  "https://images.unsplash.com/photo-1631193816258-28b44b21e78b?crop=entropy&cs=srgb&fm=jpg&q=85";
const RESORT_IMG =
  "https://images.unsplash.com/photo-1623718649591-311775a30c43?crop=entropy&cs=srgb&fm=jpg&q=85";
const HOTEL_IMG =
  "https://images.unsplash.com/photo-1646991761123-d83ce47c30c9?crop=entropy&cs=srgb&fm=jpg&q=85";

export default function Services() {
  const { t } = useLanguage();

  const pillars = [
    {
      img: CORPORATE_IMG,
      alt: "Modern corporate office infrastructure",
      tag: t.services.c1tag,
      title: t.services.c1title,
      items: t.services.c1items,
      icons: [Building2, ShieldCheck, Radar, ServerCog],
      stack: t.services.c1stack,
      link: t.services.c1link,
      testid: "service-corporate",
      accent: "cyan",
    },
    {
      img: RESORT_IMG,
      secondaryImg: HOTEL_IMG,
      alt: "Wide resort pool area with palm trees",
      tag: t.services.c2tag,
      title: t.services.c2title,
      items: t.services.c2items,
      icons: [Wifi, Gauge, RadioTower, Network],
      stack: t.services.c2stack,
      link: t.services.c2link,
      testid: "service-hospitality",
      accent: "emerald",
    },
  ];

  return (
    <section id="services" className="relative py-28 sm:py-32" data-testid="services-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHead eyebrow={t.services.eyebrow} title={t.services.h2} body={t.services.body} testid="services-title" />

        <div className="mt-16 grid gap-8 md:grid-cols-2">
          {pillars.map((p, idx) => (
            <motion.article
              key={p.testid}
              {...reveal}
              transition={{ duration: 0.8, delay: idx * 0.14, ease: EASE }}
              data-testid={p.testid}
              className="group relative flex flex-col overflow-hidden rounded-3xl border border-slate-800/60 bg-slate-900/40 backdrop-blur-sm transition-all duration-500 hover:-translate-y-1.5 hover:border-orange-400/40 hover:shadow-[0_20px_60px_rgba(234,88,12,0.15)]"
            >
              <div className="relative h-60 overflow-hidden sm:h-72">
                <img
                  src={p.img}
                  alt={p.alt}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/30 to-transparent" />
                <div
                  className={`absolute inset-0 mix-blend-overlay ${
                    p.accent === "cyan"
                      ? "bg-gradient-to-tr from-orange-500/20 via-transparent to-transparent"
                      : "bg-gradient-to-tr from-amber-500/20 via-transparent to-transparent"
                  }`}
                />
                <span className="absolute left-5 top-5 rounded-full border border-slate-500/40 bg-slate-950/70 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-300 backdrop-blur-md">
                  {p.tag}
                </span>
                {p.secondaryImg && (
                  <div className="absolute bottom-5 right-5 hidden h-28 w-44 rotate-2 overflow-hidden rounded-xl border border-slate-600/50 shadow-2xl transition-transform duration-500 group-hover:rotate-0 sm:block">
                    <img
                      src={p.secondaryImg}
                      alt="Premium European hotel lobby"
                      loading="lazy"
                      className="h-full w-full object-cover"
                    />
                  </div>
                )}
              </div>

              <div className="flex flex-1 flex-col p-7 sm:p-9">
                <h3 className="font-display text-2xl font-bold tracking-tight text-slate-50 sm:text-3xl">
                  {p.title}
                </h3>

                <ul className="mt-7 grid flex-1 gap-3.5 sm:grid-cols-2">
                  {p.items.map((item, i) => {
                    const Icon = p.icons[i];
                    return (
                      <li key={i} className="flex items-start gap-3 text-sm leading-relaxed text-slate-300">
                        <span
                          className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-slate-700/50 bg-slate-950/60 ${
                            p.accent === "cyan" ? "text-orange-300" : "text-amber-300"
                          }`}
                        >
                          <Icon className="h-3.5 w-3.5" />
                        </span>
                        {item}
                      </li>
                    );
                  })}
                </ul>

                <div className="mt-8 flex items-center justify-between border-t border-slate-800/60 pt-6">
                  <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">
                    {p.stack}
                  </span>
                  <button
                    data-testid={`${p.testid}-link`}
                    onClick={() => scrollToId("#contact")}
                    className="group/link flex items-center gap-1.5 text-sm font-semibold text-orange-300 transition-colors hover:text-orange-200"
                  >
                    {p.link}
                    <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover/link:translate-x-0.5 group-hover/link:-translate-y-0.5" />
                  </button>
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
